import { useState } from "react";
import { TYPOGRAPHY, RADIUS } from "../theme/tokens";
import { useThemeContext } from "../theme/ThemeContext";

// -- Star Alliance member airlines (IATA codes) ---------------------------------
export const SA_AIRLINES = [
  { code: "AC", name: "Air Canada" },
  { code: "CA", name: "Air China" },
  { code: "AI", name: "Air India" },
  { code: "NZ", name: "Air New Zealand" },
  { code: "NH", name: "All Nippon Airways (ANA)" },
  { code: "OZ", name: "Asiana Airlines" },
  { code: "OS", name: "Austrian Airlines" },
  { code: "SN", name: "Brussels Airlines" },
  { code: "CM", name: "Copa Airlines" },
  { code: "OU", name: "Croatia Airlines" },
  { code: "MS", name: "EgyptAir" },
  { code: "ET", name: "Ethiopian Airlines" },
  { code: "BR", name: "EVA Air" },
  { code: "AZ", name: "ITA Airways" },
  { code: "LO", name: "LOT Polish Airlines" },
  { code: "LH", name: "Lufthansa" },
  { code: "SK", name: "Scandinavian Airlines (SAS)" },
  { code: "ZH", name: "Shenzhen Airlines" },
  { code: "SQ", name: "Singapore Airlines" },
  { code: "SA", name: "South African Airways" },
  { code: "LX", name: "SWISS International Air Lines" },
  { code: "TP", name: "TAP Air Portugal" },
  { code: "TG", name: "Thai Airways" },
  { code: "TK", name: "Turkish Airlines" },
  { code: "UA", name: "United Airlines" },
  { code: "A3", name: "Aegean Airlines" },
];

// Lufthansa Group subsidiaries — LHG login sees all of these
export const LHG_CARRIERS = ["LH", "OS", "SN", "4Y", "LX", "WK"];

// -- Airline logo helpers -------------------------------------------------------
export const AIRLINE_COLORS = {
  AC: { bg: "#D22630", text: "#fff" }, CA: { bg: "#CC0000", text: "#fff" },
  AI: { bg: "#EF2B2D", text: "#fff" }, NZ: { bg: "#00539B", text: "#fff" },
  NH: { bg: "#13448C", text: "#fff" }, OZ: { bg: "#00538B", text: "#fff" },
  OS: { bg: "#C8102E", text: "#fff" }, SN: { bg: "#003087", text: "#fff" },
  CM: { bg: "#00529B", text: "#fff" }, OU: { bg: "#003087", text: "#fff" },
  MS: { bg: "#007749", text: "#fff" }, ET: { bg: "#009639", text: "#fff" },
  BR: { bg: "#00703C", text: "#fff" }, AZ: { bg: "#007A4D", text: "#fff" }, LO: { bg: "#003087", text: "#fff" },
  LH: { bg: "#05164D", text: "#FFD700" }, SK: { bg: "#003087", text: "#fff" },
  ZH: { bg: "#003087", text: "#fff" }, SQ: { bg: "#003F87", text: "#FFD700" },
  SA: { bg: "#007AC0", text: "#fff" }, LX: { bg: "#C8102E", text: "#fff" },
  TP: { bg: "#006600", text: "#fff" }, TG: { bg: "#6B0D8F", text: "#FFD700" },
  TK: { bg: "#C8102E", text: "#fff" }, UA: { bg: "#003087", text: "#FFD700" },
  A3: { bg: "#003087", text: "#fff" },
};

// Map codes that need a different IATA for logo lookup
const LOGO_CODE_MAP = {
  LHG: "LH", // Lufthansa Group → use LH logo
  "4Y": "LH", // Eurowings → LH group
  WK:  "LH", // Edelweiss → LH group
};

export function AirlineLogo({ iata, size = 30 }) {
  const raw    = (iata || "").toUpperCase().trim();
  const code   = raw.slice(0, raw === "LHG" ? 3 : 2); // keep LHG as-is for color lookup
  const logoCode = LOGO_CODE_MAP[code] || code;
  const colors = AIRLINE_COLORS[code] || { bg: "#444", text: "#fff" };
  const [imgOk, setImgOk] = useState(true);

  const imgUrl = `https://www.gstatic.com/flights/airline_logos/70px/${logoCode}.png`;

  const containerStyle = {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: size, height: size, borderRadius: RADIUS.sm, flexShrink: 0,
    overflow: "hidden", userSelect: "none",
    background: imgOk ? "#fff" : colors.bg,
    border: imgOk ? "1.5px solid rgba(255,255,255,0.15)" : "1.5px solid rgba(255,255,255,0.2)",
  };

  return (
    <span style={containerStyle}>
      {imgOk
        ? <img
            src={imgUrl}
            alt={logoCode}
            onError={() => setImgOk(false)}
            style={{ width: "85%", height: "85%", objectFit: "contain", display: "block" }}
          />
        : <span style={{
            color: colors.text, fontSize: Math.round(size * 0.38), fontWeight: 900,
            fontFamily: "'Arial Black',Arial,sans-serif", letterSpacing: "-0.5px",
          }}>
            {code.slice(0,2) || "??"}
          </span>
      }
    </span>
  );
}

// -- Star Alliance logo star -- faithfully traced from the official Pentagram mark.
export function StarMark({ size = 18, color = "#92959B", gradient = false }) {
  const arms = [
    { tip:[50.00,3.00],  li:[40.01,36.25], ri:[59.99,36.25], r:[50.00,47.00] },
    { tip:[94.70,35.48], li:[59.99,36.25], ri:[66.17,55.25], r:[52.85,49.07] },
    { tip:[77.63,88.02], li:[66.17,55.25], ri:[50.00,67.00], r:[51.76,52.43] },
    { tip:[22.37,88.02], li:[50.00,67.00], ri:[33.83,55.25], r:[48.24,52.43] },
    { tip:[5.30,35.48],  li:[33.83,55.25], ri:[40.01,36.25], r:[47.15,49.07] },
  ];

  const light = gradient ? "#CDD0D3" : color;
  const dark  = gradient ? "#4a4a50" : color;
  const mid   = gradient ? "#92959B" : color;

  const leftColors  = [light, dark,  light, dark,  light];
  const rightColors = [mid,   light, dark,  light, dark ];

  const pt = ([x,y]) => x.toFixed(2) + "," + y.toFixed(2);

  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ display: "inline-block", flexShrink: 0 }}>
      {arms.map(({ tip, li, ri, r }, i) => (
        <g key={i}>
          <path d={"M"+pt(tip)+" L"+pt(r)+" L"+pt(li)+"Z"} fill={leftColors[i]}  />
          <path d={"M"+pt(tip)+" L"+pt(ri)+" L"+pt(r)+"Z"} fill={rightColors[i]} />
        </g>
      ))}
    </svg>
  );
}

// -- Form input style helper (takes SA to work in extracted files) --------------
export function inputStyle(focused, SA) {
  const isDark = SA && SA.black === "#060912"; // dark theme check
  return {
    backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
    background: isDark ? "rgba(0,6,18,0.65)" : "rgba(255,255,255,0.96)",
    border: `1px solid ${focused
      ? (isDark ? "rgba(255,255,255,0.30)" : "rgba(80,100,160,0.45)")
      : (isDark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.15)")}`,
    borderRadius: RADIUS.sm, color: SA.white, padding: "10px 13px", fontSize: TYPOGRAPHY.note, width: "100%",
    outline: "none", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
    boxSizing: "border-box", letterSpacing: "0.04em", transition: "border-color 0.15s",
  };
}

export function FieldLabel({ children, SA }) {
  return (
    <label style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.16em", textTransform: "uppercase",
      fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontWeight: 400, display: "block", marginBottom: 5 }}>
      {children}
    </label>
  );
}

export function TimeInput({ label, value, onChange, name, disabled, style: extraStyle, SA }) {
  const [f, setF] = useState(false);
  const handleInput = (e) => {
    let raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
    let formatted = raw;
    if (raw.length >= 3) formatted = raw.slice(0, 2) + ":" + raw.slice(2);
    onChange({ target: { name, value: formatted } });
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, ...extraStyle }}>
      <FieldLabel SA={SA}>{label}</FieldLabel>
      <input
        type="text"
        inputMode="numeric"
        placeholder="HH:MM"
        name={name}
        value={value}
        onChange={handleInput}
        disabled={disabled}
        onFocus={() => setF(true)}
        onBlur={() => setF(false)}
        style={{ ...inputStyle(f, SA), opacity: disabled ? 0.3 : 1 }}
      />
    </div>
  );
}

export function TextInput({ label, value, onChange, name, placeholder, flex, inputMode, SA }) {
  const [f, setF] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: flex || 1 }}>
      <FieldLabel SA={SA}>{label}</FieldLabel>
      <input name={name} value={value} onChange={onChange} placeholder={placeholder || ""}
        onFocus={() => setF(true)} onBlur={() => setF(false)}
        inputMode={inputMode || "text"}
        style={{ ...inputStyle(f, SA), textTransform: "uppercase" }} />
    </div>
  );
}

export function NumberInput({ label, value, onChange, name, SA }) {
  const [f, setF] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <FieldLabel SA={SA}>{label}</FieldLabel>
      <input type="text" inputMode="numeric" name={name} value={value} onChange={onChange}
        onFocus={() => setF(true)} onBlur={() => setF(false)} autoComplete="off" autoCorrect="off" style={inputStyle(f, SA)} />
    </div>
  );
}

export function SectionDivider({ children, SA }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "20px 0 14px" }}>
      <div style={{ height: 1, flex: 1, background: SA.borderGray }} />
      <span style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.18em", textTransform: "uppercase",
        fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>{children}</span>
      <div style={{ height: 1, flex: 1, background: SA.borderGray }} />
    </div>
  );
}

// -- Button component ----------------------------------------------------------
// variant: primary | secondary | danger | icon | ghost | pill | tile
// size:    sm | md | lg  (icon variant ignores size — always 28×28 circle)
// active:  used by pill and tile variants for selected state
export function Button({ variant = "secondary", size = "md", active = false,
  disabled = false, fullWidth = false, onClick, onKeyDown, children, style,
  type = "button", title, className, ...rest }) {
  const { SA, tokens, TYPOGRAPHY, isDark } = useThemeContext();

  const base = {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    cursor: disabled ? "not-allowed" : "pointer",
    outline: "none", boxSizing: "border-box",
    fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
    transition: "all 0.15s",
    ...(fullWidth && { width: "100%" }),
    ...(disabled && { opacity: 0.5 }),
  };

  const sizeMap = {
    sm: { padding: "7px 12px",  fontSize: TYPOGRAPHY.caption, borderRadius: RADIUS.sm },
    md: { padding: "11px 14px", fontSize: TYPOGRAPHY.body,    borderRadius: RADIUS.md },
    lg: { padding: "13px",      fontSize: TYPOGRAPHY.label,   borderRadius: RADIUS.md },
  };

  const variantMap = {
    primary: {
      backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
      background: isDark ? "rgba(91,142,255,0.75)" : "#2563EB",
      border: isDark ? "1px solid rgba(255,255,255,0.22)" : "1px solid rgba(255,255,255,0.30)",
      boxShadow: isDark
        ? "inset 0 1px 0 rgba(255,255,255,0.28), 0 4px 16px rgba(91,142,255,0.35)"
        : "0 4px 16px rgba(37,99,235,0.30)",
      color: "#fff", fontWeight: 600,
    },
    secondary: {
      background: "transparent", color: SA.silver,
      border: `1px solid ${SA.borderGray}`, fontWeight: 400,
    },
    danger: {
      background: tokens.dangerSurface, color: tokens.dangerText,
      border: `1px solid ${tokens.dangerSurface2}`, fontWeight: 600,
    },
    icon: {
      width: 28, height: 28, minWidth: 28, minHeight: 28, padding: 0, flexShrink: 0,
      borderRadius: RADIUS.circle,
      background: SA.medGray, color: SA.silver, border: "none", fontWeight: 400,
    },
    ghost: {
      background: "none", color: SA.silver, border: "none", fontWeight: 400,
    },
    pill: {
      background: active ? (isDark ? SA.medGray : "rgba(37,99,235,0.12)") : "transparent",
      color: active ? (isDark ? SA.white : SA.accent) : SA.silver,
      border: `1px solid ${active ? (isDark ? SA.borderGray : SA.accent) : "transparent"}`,
      borderRadius: RADIUS.pill, fontWeight: active ? 600 : 400,
      padding: "5px 14px", fontSize: TYPOGRAPHY.caption,
    },
    tile: {
      background: active ? SA.medGray : "transparent",
      color: active ? SA.white : SA.silver,
      border: "none", borderRadius: 0, width: "100%",
      justifyContent: "flex-start", fontWeight: active ? 500 : 400,
    },
  };

  const sz = variant === "icon" ? {} : (sizeMap[size] || sizeMap.md);
  const vr = variantMap[variant] || variantMap.secondary;

  // When the icon variant receives a close symbol, swap it for a pixel-perfect SVG
  // so the × character's optical-centering quirks don't affect alignment.
  const isCloseSymbol = variant === "icon" && (children === "×" || children === "✕" || children === "x" || children === "✗");
  const content = isCloseSymbol
    ? <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
        <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    : children;

  return (
    <button type={type} disabled={disabled} onClick={onClick} onKeyDown={onKeyDown}
      title={title} className={className}
      style={{ ...base, ...sz, ...vr, ...style }} {...rest}>
      {content}
    </button>
  );
}

export function AirlineSelector({ value, onChange, SA }) {
  const [query, setQuery]   = useState(value || "");
  const [open,  setOpen]    = useState(false);
  const [focused, setFocused] = useState(false);

  // Filter: match on code or name, case-insensitive; UA always pinned to top; LHG excluded (login group only)
  const matches = SA_AIRLINES.filter(a =>
    a.code !== "LHG" && (
      a.code.toLowerCase().includes(query.toLowerCase()) ||
      a.name.toLowerCase().includes(query.toLowerCase())
    )
  );
  const ua = matches.find(a => a.code === "UA");
  const rest = matches.filter(a => a.code !== "UA");
  const filtered = ua ? [ua, ...rest] : rest;

  const select = (airline) => {
    setQuery(airline.code);
    onChange(airline.code);
    setOpen(false);
  };

  const handleInput = (val) => {
    const upper = val.toUpperCase().slice(0, 2);
    setQuery(upper);
    onChange(upper);
    setOpen(true);
  };

  const handleBlur = () => {
    setTimeout(() => { setOpen(false); setFocused(false); }, 150);
  };

  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      <FieldLabel SA={SA}>Airline</FieldLabel>
      <input
        value={query}
        onChange={e => handleInput(e.target.value)}
        onFocus={() => { setOpen(true); setFocused(true); }}
        onBlur={handleBlur}
        placeholder="UA"
        maxLength={2}
        style={{
          ...inputStyle(false, SA),
          textTransform: "uppercase",
          letterSpacing: "0.14em",
          width: 52,
          border: `1px solid ${focused ? SA.silver : SA.borderGray}`
        }}
      />
      {open && query.length >= 0 && filtered.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 500,
          background: SA.black === "#060912" ? "rgba(22,28,50,0.96)" : "rgba(255,255,255,0.96)",
          backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
          border: `1px solid ${SA.borderGray}`,
          borderTop: "none", borderRadius: "0 0 8px 8px",
          maxHeight: 200, overflowY: "auto", width: 220
        }}>
          {filtered.map(a => (
            <Button key={a.code} onMouseDown={() => select(a)}
              style={{
                width: "100%", background: "none", border: "none",
                borderBottom: `1px solid ${SA.borderGray}`,
                padding: "10px 12px", textAlign: "left",
                alignItems: "center", gap: 10
              }}
              onMouseEnter={e => e.currentTarget.style.background = SA.medGray}
              onMouseLeave={e => e.currentTarget.style.background = "none"}>
              <span style={{ fontSize: TYPOGRAPHY.caption, color: SA.lightSilver, fontFamily: "'IBM Plex Mono',monospace",
                fontWeight: 600, width: 24, flexShrink: 0 }}>{a.code}</span>
              <span style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                letterSpacing: "0.06em" }}>{a.name}</span>
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
